// frontend/src/components/LOS/Niyogin/NiyoginAAStatus.tsx
//
// Account Aggregator (AA) Status + BSA — Step 2
// ===============================================
// Picks up where NiyoginAAConsent left off.
// Reads consent_handle_id + customer_id from sessionStorage (set by NiyoginAAConsent).
//
// Automated pipeline (runs on mount):
//   Step 2 — Poll GET /api/niyogin/aa/consent/status/:handleId/:custId
//             until consentStatus == "ACCEPTED" → get consentId
//   Step 3 — GET /api/niyogin/aa/consent/details/:consentId
//             → get date_range_from / date_range_to
//   Step 4 — POST /api/niyogin/aa/fi/request/
//             → get sessionId
//   Step 5 — Poll GET /api/niyogin/aa/data/status/:consentId/:sessionId/:handleId/:custId
//             until fiRequestStatus == "READY" → get linkRefNumbers[] (one per linked bank account)
//   Step 6 — Poll GET /api/niyogin/aa/bsa/status/:sessionId/:firstLinkRefNumber
//             until bsaStatus == "COMPLETED"
//   Step 7 — POST /api/niyogin/aa/bsa/report/  { session_id, link_ref_numbers: [...] }
//             → saves one BankStatementAnalysisResponse per account → onDone()
//
// On any error the user sees the error + a Retry button.

import React, { useEffect, useRef, useState } from "react";
import axios from "@/lib/axios";
import { API_BASE_URL } from "@/config";
import JourneyHeader from "@/components/JourneyHeader";

interface Props {
  value?: any;
  /** When true (builder/preview mode) skip all API calls and show a static placeholder */
  isBuilderMode?: boolean;
  onChange?: (val: any) => void;
  disabled?: boolean;
  registerSubmit?: (handler: () => Promise<boolean>) => void;
  onDone?: () => void;
}

// ── Constants ──────────────────────────────────────────────────────────────────
const POLL_INTERVAL_MS = 4000;
const MAX_CONSENT_POLLS = 20;   // 20 × 4s = 80s
const MAX_DATA_POLLS    = 20;
const MAX_BSA_POLLS     = 20;

// ── Step labels shown in the progress UI ──────────────────────────────────────
const STEP_LABELS = [
  "Checking consent status…",
  "Fetching consent details…",
  "Requesting financial data…",
  "Waiting for data to be ready…",
  "Analysing bank statements…",
  "Fetching BSA report…",
  "Complete ✅",
];

type StepStatus = "pending" | "active" | "done" | "error";

interface StepState {
  label: string;
  status: StepStatus;
}

// ── Helpers ────────────────────────────────────────────────────────────────────
function sleep(ms: number) {
  return new Promise<void>((r) => setTimeout(r, ms));
}

async function pollUntil<T>(
  fn: () => Promise<T | null>,
  maxAttempts: number,
  intervalMs: number
): Promise<T> {
  for (let i = 0; i < maxAttempts; i++) {
    const result = await fn();
    if (result !== null) return result;
    await sleep(intervalMs);
  }
  throw new Error("Polling timed out — please try again.");
}

// ── Component ──────────────────────────────────────────────────────────────────
const NiyoginAAStatus: React.FC<Props> = ({ onDone, isBuilderMode = false }) => {
  const startedRef  = useRef(false);
  // Guard: ensure onDone() is called at most once per mount, even if the
  // component re-renders or the REPORT_SAVED branch fires multiple times.
  const doneCalledRef = useRef(false);

  const [steps, setSteps] = useState<StepState[]>(
    STEP_LABELS.map((label, i) => ({
      label,
      status: i === 0 ? "active" : "pending",
    }))
  );
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Read IDs set by NiyoginAAConsent (stored in sessionStorage).
  // If sessionStorage was cleared (e.g. page refresh), we fall back to
  // fetching customerId from the backend via GET /api/niyogin/aa/customer-id/.
  const [consentHandleId, setConsentHandleId] = useState<string>(
    sessionStorage.getItem("niyogin_aa_consent_handle_id") || ""
  );
  const [customerId, setCustomerId] = useState<string>(
    sessionStorage.getItem("niyogin_aa_customer_id") || ""
  );

  // ── Step helpers ─────────────────────────────────────────────────────────────
  const markStep = (index: number, status: StepStatus) => {
    setSteps((prev) =>
      prev.map((s, i) => (i === index ? { ...s, status } : s))
    );
  };

  const activateStep = (index: number) => {
    setSteps((prev) =>
      prev.map((s, i) => ({
        ...s,
        status: i === index ? "active" : i < index ? "done" : s.status,
      }))
    );
  };

  // ── Main pipeline ─────────────────────────────────────────────────────────────
  const runPipeline = async (handleId?: string, custId?: string) => {
    // Use passed-in values (from fallback fetch) or fall back to state
    const effectiveHandleId  = handleId  || consentHandleId;
    const effectiveCustId    = custId    || customerId;
    try {
      // ── Step 2: Poll consent status ──────────────────────────────────────────
      activateStep(0);
      const consentId = await pollUntil(async () => {
        const res = await axios.get(
          `${API_BASE_URL}/api/niyogin/aa/consent/status/${effectiveHandleId}/${effectiveCustId}/`
        );
        if (res.data?.success === false) {
          throw new Error(res.data?.error || "Consent status check failed.");
        }
        const { consent_status, consent_id } = res.data || {};
        if (consent_status === "ACCEPTED" && consent_id) return consent_id as string;
        return null; // keep polling
      }, MAX_CONSENT_POLLS, POLL_INTERVAL_MS);

      markStep(0, "done");

      // ── Step 3: Consent details ──────────────────────────────────────────────
      activateStep(1);
      const detailsRes = await axios.get(
        `${API_BASE_URL}/api/niyogin/aa/consent/details/${consentId}/`
      );
      if (detailsRes.data?.success === false) {
        throw new Error(detailsRes.data?.error || "Failed to fetch consent details.");
      }
      const { date_range_from, date_range_to } = detailsRes.data || {};
      markStep(1, "done");

      // ── Step 4: FI Request ───────────────────────────────────────────────────
      activateStep(2);
      const fiRes = await axios.post(`${API_BASE_URL}/api/niyogin/aa/fi/request/`, {
        customer_id:       effectiveCustId,
        consent_handle_id: effectiveHandleId,
        consent_id:        consentId,
        date_range_from,
        date_range_to,
      });
      if (fiRes.data?.success === false) {
        throw new Error(fiRes.data?.error || "FI request failed.");
      }
      const sessionId = fiRes.data?.session_id as string;
      markStep(2, "done");

      // ── Step 5: Poll data status ─────────────────────────────────────────────
      // Returns ALL linkRefNumbers (one per linked bank account, all status READY)
      activateStep(3);
      const linkRefNumbers = await pollUntil(async () => {
        const res = await axios.get(
          `${API_BASE_URL}/api/niyogin/aa/data/status/${consentId}/${sessionId}/${effectiveHandleId}/${encodeURIComponent(effectiveCustId)}/`
        );
        if (res.data?.success === false) {
          throw new Error(res.data?.error || "Data status check failed.");
        }
        const { fi_request_status, link_ref_numbers, link_ref_number } = res.data || {};
        // Accept either the new array field or the legacy singular field
        const refs: string[] = link_ref_numbers?.length
          ? link_ref_numbers
          : link_ref_number
          ? [link_ref_number]
          : [];
        if (fi_request_status === "READY" && refs.length > 0) return refs;
        return null;
      }, MAX_DATA_POLLS, POLL_INTERVAL_MS);

      markStep(3, "done");

      // ── Step 6: Poll BSA status ──────────────────────────────────────────────
      // All accounts share the same session — poll using the first linkRefNumber
      activateStep(4);
      const firstRef = linkRefNumbers[0];
      await pollUntil(async () => {
        const res = await axios.get(
          `${API_BASE_URL}/api/niyogin/aa/bsa/status/${sessionId}/${firstRef}/`
        );
        if (res.data?.success === false) {
          throw new Error(res.data?.error || "BSA status check failed.");
        }
        const { bsa_status } = res.data || {};
        // Niyogin returns "READY" or "COMPLETED" to indicate BSA analysis is done
        if (bsa_status === "COMPLETED" || bsa_status === "READY") return true;
        return null;
      }, MAX_BSA_POLLS, POLL_INTERVAL_MS);

      markStep(4, "done");

      // ── Step 7: Fetch BSA report for ALL linkRefNumbers ──────────────────────
      // Backend loops over each ref, calls /bsa/json-report, saves each separately
      activateStep(5);
      const reportRes = await axios.post(`${API_BASE_URL}/api/niyogin/aa/bsa/report/`, {
        session_id:       sessionId,
        link_ref_numbers: linkRefNumbers,
      });
      if (reportRes.data?.success === false) {
        // Surface the first per-ref error if available
        const firstErr = reportRes.data?.errors?.[0]?.error;
        throw new Error(firstErr || reportRes.data?.error || "Failed to fetch BSA report.");
      }
      const savedCount = (reportRes.data?.reports ?? []).length;
      console.info(
        `[NiyoginAAStatus] BSA reports saved: ${savedCount}/${linkRefNumbers.length} accounts`
      );
      markStep(5, "done");

      // ── All done ─────────────────────────────────────────────────────────────
      activateStep(6);
      markStep(6, "done");

      // Clean up sessionStorage
      sessionStorage.removeItem("niyogin_aa_consent_handle_id");
      sessionStorage.removeItem("niyogin_aa_customer_id");

      setDone(true);
      // Do NOT auto-call onDone here — the user must click Continue to advance.
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        (err instanceof Error ? err.message : String(err));
      console.error("[NiyoginAAStatus] Pipeline error:", msg, err?.response?.data);
      setError(msg);
      // Mark the currently-active step as error
      setSteps((prev) =>
        prev.map((s) => (s.status === "active" ? { ...s, status: "error" } : s))
      );
    }
  };

  // ── Auto-start on mount ───────────────────────────────────────────────────────
  // If consentHandleId or customerId is missing from sessionStorage (e.g. page
  // refresh), fetch both from the backend via GET /api/niyogin/aa/session/latest/
  // before starting the pipeline.
  // If the session is already fully complete (REPORT_SAVED), skip the pipeline
  // and call onDone() immediately.
  useEffect(() => {
    if (isBuilderMode) return;
    if (startedRef.current) return;
    startedRef.current = true;

    const start = async () => {
      let resolvedHandleId   = consentHandleId;
      let resolvedCustomerId = customerId;

      // Always fetch latest session to check if already complete
      try {
        const res = await axios.get(`${API_BASE_URL}/api/niyogin/aa/session/latest/`);
        if (res.data?.success) {
          // If the pipeline already completed fully AND the journey was already
          // advanced, just show the done state — do NOT call onDone() here
          // because that would trigger fetchCurrentPage → same page → infinite loop.
          // The journey was already advanced by aa_bsa_report on the previous run.
          // JourneyPage will naturally show the next page on the next navigation.
          if (res.data.status === "REPORT_SAVED") {
            setSteps(STEP_LABELS.map((label) => ({ label, status: "done" as StepStatus })));
            setDone(true);
            // Journey was already advanced on the previous run — show done state
            // so the user can click Continue to proceed.
            return;
          }
          if (!resolvedHandleId && res.data.consent_handle_id) {
            resolvedHandleId = res.data.consent_handle_id;
            sessionStorage.setItem("niyogin_aa_consent_handle_id", resolvedHandleId);
            setConsentHandleId(resolvedHandleId);
          }
          if (!resolvedCustomerId && res.data.customer_id) {
            resolvedCustomerId = res.data.customer_id;
            sessionStorage.setItem("niyogin_aa_customer_id", resolvedCustomerId);
            setCustomerId(resolvedCustomerId);
          }
        }
      } catch (err) {
        console.warn("[NiyoginAAStatus] Could not fetch latest AA session from backend:", err);
      }

      if (!resolvedHandleId) {
        setError(
          "Missing consent handle ID. Please go back and complete the consent step."
        );
        setSteps((prev) =>
          prev.map((s, i) => (i === 0 ? { ...s, status: "error" } : s))
        );
        return;
      }

      if (!resolvedCustomerId) {
        setError(
          "Could not determine your customer ID. Please go back and complete the consent step."
        );
        setSteps((prev) =>
          prev.map((s, i) => (i === 0 ? { ...s, status: "error" } : s))
        );
        return;
      }

      runPipeline(resolvedHandleId, resolvedCustomerId);
    };

    start();
  }, []);

  // ── Retry ─────────────────────────────────────────────────────────────────────
  const handleRetry = () => {
    startedRef.current = false;
    setError(null);
    setDone(false);
    setSteps(STEP_LABELS.map((label, i) => ({ label, status: i === 0 ? "active" : "pending" })));
    // Re-run the full start() logic so IDs are re-resolved from sessionStorage/backend
    startedRef.current = false;
    // Trigger useEffect re-run by resetting the ref and forcing a re-mount via key
    // Instead, directly re-run the start logic inline:
    const retryStart = async () => {
      let resolvedHandleId  = sessionStorage.getItem("niyogin_aa_consent_handle_id") || "";
      let resolvedCustomerId = sessionStorage.getItem("niyogin_aa_customer_id") || "";

      if (!resolvedHandleId || !resolvedCustomerId) {
        try {
          const res = await axios.get(`${API_BASE_URL}/api/niyogin/aa/session/latest/`);
          if (res.data?.success) {
            if (!resolvedHandleId && res.data.consent_handle_id) {
              resolvedHandleId = res.data.consent_handle_id;
              sessionStorage.setItem("niyogin_aa_consent_handle_id", resolvedHandleId);
              setConsentHandleId(resolvedHandleId);
            }
            if (!resolvedCustomerId && res.data.customer_id) {
              resolvedCustomerId = res.data.customer_id;
              sessionStorage.setItem("niyogin_aa_customer_id", resolvedCustomerId);
              setCustomerId(resolvedCustomerId);
            }
          }
        } catch (err) {
          console.warn("[NiyoginAAStatus] Retry: Could not fetch latest AA session:", err);
        }
      }

      if (!resolvedHandleId || !resolvedCustomerId) {
        setError("Missing consent handle ID. Please go back and complete the consent step.");
        setSteps((prev) => prev.map((s, i) => (i === 0 ? { ...s, status: "error" } : s)));
        return;
      }

      runPipeline(resolvedHandleId, resolvedCustomerId);
    };
    retryStart();
  };

  // ── Step icon ─────────────────────────────────────────────────────────────────
  const StepIcon: React.FC<{ status: StepStatus }> = ({ status }) => {
    if (status === "done") {
      return <span className="text-intellend-green font-bold text-lg">✓</span>;
    }
    if (status === "error") {
      return <span className="text-red-500 font-bold text-lg">✗</span>;
    }
    if (status === "active") {
      return (
        <svg className="animate-spin h-5 w-5 text-intellend-green" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
        </svg>
      );
    }
    // pending
    return <span className="h-5 w-5 rounded-full border-2 border-gray-300 inline-block" />;
  };

  // ── Builder/preview mode: static placeholder ──────────────────────────────────
  if (isBuilderMode) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-400">
        <div className="text-2xl mb-1">📊</div>
        <p className="font-semibold text-gray-500">Niyogin: AA Status + BSA</p>
        <p className="text-xs mt-1">Automatically polls consent status, submits FI request, and fetches BSA report during the journey.</p>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-intellend-white">
      <JourneyHeader />
      <main className="container mx-auto px-4 py-8 max-w-md">
        <div className="bg-white shadow-md rounded-lg p-8 space-y-6">
          {/* Header */}
          <div className="text-center space-y-2">
            <div className="text-5xl">{done ? "🎉" : error ? "⚠️" : "📊"}</div>
            <h2 className="text-xl font-bold text-gray-800">
              {done
                ? "Bank Analysis Complete!"
                : error
                ? "Analysis Failed"
                : "Analysing Bank Statements…"}
            </h2>
            {!done && !error && (
              <p className="text-sm text-gray-500">
                Please wait while we securely fetch and analyse your bank data.
              </p>
            )}
          </div>

          {/* Progress steps */}
          <div className="space-y-3">
            {steps.map((step, i) => (
              <div
                key={i}
                className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                  step.status === "active"
                    ? "bg-blue-50 border border-blue-200"
                    : step.status === "done"
                    ? "bg-green-50 border border-green-200"
                    : step.status === "error"
                    ? "bg-red-50 border border-red-200"
                    : "bg-gray-50 border border-gray-100"
                }`}
              >
                <div className="flex-shrink-0 w-6 flex items-center justify-center">
                  <StepIcon status={step.status} />
                </div>
                <span
                  className={`text-sm font-medium ${
                    step.status === "active"
                      ? "text-blue-700"
                      : step.status === "done"
                      ? "text-green-700"
                      : step.status === "error"
                      ? "text-red-700"
                      : "text-gray-400"
                  }`}
                >
                  {step.label}
                </span>
              </div>
            ))}
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-1">
              <p className="text-sm font-semibold text-red-700">❌ Error</p>
              <p className="text-sm text-red-600">{error}</p>
              <p className="text-xs text-red-500">Please retry or contact support.</p>
            </div>
          )}

          {/* Retry button */}
          {error && (
            <button
              onClick={handleRetry}
              className="w-full py-3 px-6 bg-intellend-green text-white font-semibold rounded-lg hover:bg-intellend-green/90 transition-colors"
            >
              Retry Analysis
            </button>
          )}

          {/* Success message + Continue button */}
          {done && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <p className="text-sm font-semibold text-green-700">
                ✅ Bank statement analysis saved successfully!
              </p>
              <p className="text-xs text-green-600 mt-1">
                Your loan application is being processed.
              </p>
            </div>
          )}
          {done && (
            <button
              onClick={() => onDone?.()}
              className="w-full py-3 px-6 bg-intellend-green text-white font-semibold rounded-lg hover:bg-intellend-green/90 transition-colors"
            >
              Continue →
            </button>
          )}
        </div>
      </main>
    </div>
  );
};

export default NiyoginAAStatus;
